import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { ShieldCheck, Plus, RefreshCw, Pencil, Trash2, X } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import useVerificationServices from "hooks/useVerificationServices";
import {
  VerificationServiceType,
  ServiceTypePayload,
  ServiceCategory,
  ServiceProvider,
  createServiceType,
  updateServiceType,
  deleteServiceType,
} from "services/verificationService.service";
import DeleteModal from "Common/DeleteModal";

const CATEGORY_COLORS: Record<ServiceCategory, string> = {
  identity: "bg-indigo-50 text-indigo-700",
  guarantor: "bg-violet-50 text-violet-700",
  background: "bg-amber-50 text-amber-700",
  health: "bg-green-50 text-green-700",
  bundle: "bg-sky-50 text-sky-700",
};

const PROVIDERS: ServiceProvider[] = ["dojah", "manual", "partner_lab"];
const CATEGORIES: ServiceCategory[] = ["identity", "guarantor", "background", "health", "bundle"];

const EMPTY_FORM: ServiceTypePayload = {
  name: "",
  description: "",
  price: 0,
  provider: "manual",
  turnaround: "",
  isActive: true,
  category: "identity",
};

function ServiceForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initial: ServiceTypePayload;
  onSubmit: (data: ServiceTypePayload) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState<ServiceTypePayload>(initial);

  const set = (field: keyof ServiceTypePayload, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
        <input
          required
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="e.g. NIN Identity Check"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <textarea
          required
          rows={2}
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          placeholder="Brief description of what this service checks"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Price (₦)</label>
          <input
            required
            type="number"
            min={0}
            value={form.price / 100}
            onChange={(e) => set("price", Math.round(Number(e.target.value) * 100))}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Turnaround</label>
          <input
            required
            value={form.turnaround}
            onChange={(e) => set("turnaround", e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g. Instant, 24-48 hours"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => set("category", e.target.value as ServiceCategory)}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Provider</label>
          <select
            value={form.provider}
            onChange={(e) => set("provider", e.target.value as ServiceProvider)}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>{p.replace("_", " ")}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isActive"
          checked={form.isActive}
          onChange={(e) => set("isActive", e.target.checked)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <label htmlFor="isActive" className="text-sm text-gray-700">Active (visible to users)</label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

const VerificationServices = () => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<VerificationServiceType | null>(null);
  const [deleting, setDeleting] = useState<VerificationServiceType | null>(null);

  const { data, isLoading, isError, refetch } = useVerificationServices();
  const services: VerificationServiceType[] = (data?.data as any) ?? [];

  const invalidate = () => qc.invalidateQueries("verification-services-catalog");

  const { mutate: doCreate, isLoading: creating } = useMutation(
    (payload: ServiceTypePayload) => createServiceType(payload),
    {
      onSuccess: () => { toast.success("Service type created"); setShowForm(false); invalidate(); },
      onError: (e: any) => { toast.error(e?.response?.data?.message ?? "Failed to create"); },
    }
  );

  const { mutate: doUpdate, isLoading: updating } = useMutation(
    ({ id, data }: { id: string; data: Partial<ServiceTypePayload> }) =>
      updateServiceType(id, data),
    {
      onSuccess: () => { toast.success("Service type updated"); setEditing(null); invalidate(); },
      onError: (e: any) => { toast.error(e?.response?.data?.message ?? "Failed to update"); },
    }
  );

  const { mutate: doDelete, isLoading: doingDelete } = useMutation(
    () => deleteServiceType(deleting!._id),
    {
      onSuccess: () => { toast.success("Service type deleted"); setDeleting(null); invalidate(); },
      onError: (e: any) => { toast.error(e?.response?.data?.message ?? "Failed to delete"); },
    }
  );

  const openEdit = (s: VerificationServiceType) => {
    setEditing(s);
    setShowForm(false);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Verification Services" pageTitle="Platform" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <button
            onClick={() => { setShowForm(true); setEditing(null); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="size-4" /> Add Service Type
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{services.length} service types</span>
        </div>

        {/* Inline create form */}
        {showForm && (
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">New Service Type</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="size-4" />
              </button>
            </div>
            <ServiceForm
              initial={EMPTY_FORM}
              onSubmit={(d) => doCreate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={creating}
            />
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Turnaround</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={7} label="Couldn't load verification services" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ShieldCheck className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No verification service types yet</p>
                  </td>
                </tr>
              ) : (
                services.map((s) => (
                  <React.Fragment key={s._id}>
                    <tr className={`hover:bg-gray-50 transition-colors ${!s.isActive ? "opacity-60" : ""}`}>
                      <td data-label="Name" data-cell="title" className="px-4 py-3">
                        <p className="font-medium text-gray-900 capitalize">{s.name}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[220px]">{s.description}</p>
                      </td>
                      <td data-label="Category" className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[s.category]}`}>
                          {s.category}
                        </span>
                      </td>
                      <td data-label="Price" className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {s.price === 0 ? <span className="text-green-600 font-medium">Free</span> : `₦${(s.price / 100).toLocaleString()}`}
                      </td>
                      <td data-label="Provider" className="px-4 py-3 text-gray-600 capitalize">{s.provider.replace("_", " ")}</td>
                      <td data-label="Turnaround" className="px-4 py-3 text-gray-600">{s.turnaround}</td>
                      <td data-label="Status" className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {s.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td data-label="Actions" data-cell="actions" className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openEdit(s)}
                            className="p-1.5 rounded text-indigo-600 hover:bg-indigo-50 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleting(s)}
                            className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editing?._id === s._id && (
                      <tr>
                        <td colSpan={7} className="px-5 py-4 bg-indigo-50 border-b border-indigo-100">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-indigo-800">Edit: {s.name}</h3>
                            <button onClick={() => setEditing(null)} className="text-indigo-400 hover:text-indigo-700">
                              <X className="size-4" />
                            </button>
                          </div>
                          <ServiceForm
                            initial={{ name: s.name, description: s.description, price: s.price, provider: s.provider, turnaround: s.turnaround, isActive: s.isActive, category: s.category }}
                            onSubmit={(d) => doUpdate({ id: s._id, data: d })}
                            onCancel={() => setEditing(null)}
                            isLoading={updating}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteModal
        show={!!deleting}
        onHide={() => setDeleting(null)}
        onDelete={() => doDelete()}
      />
    </React.Fragment>
  );
};

export default VerificationServices;
